import { createReducer, createAction } from '@reduxjs/toolkit';
import { backendCall, createApiCall, eventCall } from '@reducers/utils';
import { setSelectedMachine } from '@reducers/machines';
import { getDemoOeeTeep } from '@components/tours/helpers/demoDataRegistry';
import type { RootState, AppThunk } from '@store/types';
import type {
  StatisticsState,
  MachineListOEE,
  MachineWithOEE,
  Machine,
  MachineStatisticsOeeTeepResponse,
  MachineOeeStatistic,
  MachineOeeTeepPerPeriodStatistic,
  NcFileStatisticsTimesResponse,
  ProductionBatchStatisticsResponse,
  ProductionBatch,
  NcFileStatisticsPaidResponse,
  MachineStatisticsPaidResponse,
  MachineEventCycleStatisticsResponse,
  MachineStatisticsOeeTeepRequestDto,
  NcFileStatisticsTimesRequestDto,
  ProductionBatchStatisticsRequestDto,
  NcFileStatisticsPaidRequestDto,
  MachineStatisticsPaidRequestDto,
  AddProductionInformationRequestDto,
  MachineEventCycleStatisticsRequestDto,
  OeeTeepStatisticsInput,
} from './types';
import { navigationRef, parseRouteParamAsNumber } from '@helpers';
import { buildOeeTeepGraphParams, buildOeeTeepListParams } from './helpers/buildStatisticsParams';

// Re-export types for consumers
export type {
  StatisticsState,
  MachineListOEE,
  MachineWithOEE,
  MachineOeeStatistic,
  MachineOeeTeepPerPeriodStatistic,
  OeeTeepStatisticsInput,
  MachineStatisticsOeeTeepResponse,
  NcFileStatisticsTimesResponse,
  ProductionBatchStatisticsResponse,
  ProductionBatch,
  NcFileStatisticsPaidResponse,
  MachineStatisticsPaidResponse,
  MachineStatsHourOeeTeep,
  MachineStatsDayOeeTeep,
  MachineStatsMonthOeeTeep,
  MachineStatsWeekOeeTeep,
  MachineStatsHourAllOeeTeep,
  MachineStatsDayAllOeeTeep,
  MachineStatsMonthAllOeeTeep,
  MachineStatsWeekAllOeeTeep,
  MachineEventCycleStatisticsResponse,
} from './types';

// === Initial State ===

const initState: StatisticsState = {
  machineListOEE: {
    data: [],
    loaded: false,
  },
};

// === Actions ===

export const clear = createAction('clear');
export const setMachineListStatistic = createAction<MachineListOEE>('statistics/setMachineListStatistic');
export const close = createAction('statistics/close');

// === Thunks ===

const getPositiveNumberParam = (value: number | string | null | undefined): number | undefined => {
  const parsed = parseRouteParamAsNumber(value);
  return typeof parsed === 'number' && Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

export const machineStatisticsOeeTeep = (data: OeeTeepStatisticsInput, signal?: AbortSignal): AppThunk<Promise<MachineStatisticsOeeTeepResponse | undefined>> => async (dispatch, getState) => {
  // Demo data guard for tour
  const demoMachineId = getPositiveNumberParam(data.machineId);
  const demoData = demoMachineId ? getDemoOeeTeep(demoMachineId) : undefined;
  if (demoData) {
    return { machineStatistics: [demoData] } as MachineStatisticsOeeTeepResponse;
  }

  const {
    machinesV2: { selectedGroup },
  } = getState() as RootState;
  const machineGroupId = getPositiveNumberParam(data.machineGroupId);
  const params: MachineStatisticsOeeTeepRequestDto = buildOeeTeepListParams({
    ...data,
    machineGroupId: machineGroupId ?? (data.forAllMachines || data.allSelected ? selectedGroup?.machineGroupId : undefined),
  });

  return await dispatch(
    backendCall(
      createApiCall((api) => api.statistics.machineStatisticsOeeTeep, {
        params,
        signal,
      })
    )
  );
};

export const ncFileStatisticsTimes = (data: NcFileStatisticsTimesRequestDto): AppThunk<Promise<NcFileStatisticsTimesResponse | undefined>> => async (dispatch) => {
  return await dispatch(
    backendCall(
      createApiCall((api) => api.statistics.ncFileStatisticsTimes, {
        params: data,
      })
    )
  );
};

export const saveNCFileStatisticsTimes = (data: AddProductionInformationRequestDto): AppThunk<Promise<unknown>> => async (dispatch) => {
  const call = createApiCall((api) => api.productionInformation.addProductionInformation, {
    params: data,
  });

  try {
    console.log('dispatch');
    const resp = await dispatch(
      eventCall({
        connectionName: 'appHub',
        eventName: 'OnAddedProductionInformation',
        eventNameError: 'OnFailedToChangeProductionInformation',
        data: call,
      })
    );
    console.log(resp, 'resp');
    return resp;
  } catch (e) {
    return { error: true, errorDetails: e };
  }
};

export const productionBatchStatistics = (data: ProductionBatchStatisticsRequestDto, signal?: AbortSignal): AppThunk<Promise<ProductionBatchStatisticsResponse | undefined>> => async (dispatch) => {
  return await dispatch(
    backendCall(
      createApiCall((api) => api.statistics.productionBatchStatistics, {
        params: data,
        ...(signal ? { signal } : {}),
      })
    )
  );
};

export const productionBatchData = (data: { id: number }): AppThunk<Promise<ProductionBatch[] | undefined>> => async (dispatch) => {
  return await dispatch(
    backendCall(
      createApiCall((api) => api.statistics.productionBatchData, {
        params: data,
      })
    )
  );
};

export const machineStatisticsOeeTeepMachineList = (data: OeeTeepStatisticsInput, signal?: AbortSignal): AppThunk<Promise<MachineStatisticsOeeTeepResponse | undefined>> => async (dispatch, getState) => {
  const {
    machines: { machines, selectedItem },
    statistics: { machineListOEE },
    machinesV2: { selectedGroup },
  } = getState() as RootState;

  dispatch(setMachineListStatistic({ data: machineListOEE.data, loaded: false }));

  const machineGroupId = getPositiveNumberParam(data.machineGroupId);
  const params: MachineStatisticsOeeTeepRequestDto = buildOeeTeepListParams({
    ...data,
    allSelected: false,
    machineGroupId: machineGroupId ?? selectedGroup?.machineGroupId,
  });

  return await dispatch(
    backendCall(
      createApiCall((api) => api.statistics.machineStatisticsOeeTeep, {
        params,
        signal,
        onSuccess: (d: MachineStatisticsOeeTeepResponse) => {
          const data: MachineWithOEE[] = [];

          const storage: Record<number, Machine> = {};
          (machines as Machine[]).forEach((i) => {
            if (i.machineId != null) {
              storage[i.machineId] = i;
            }
          });

          const processedIds = new Set<number>();

          const machineStatistics = (d?.machineStatistics ?? []) as MachineOeeStatistic[];
          machineStatistics.forEach((i) => {
            const id = i.machineId;
            if (id == null) return;
            processedIds.add(id);
            const machine = storage[id];
            if (machine) {
              data.push({
                ...machine,
                machineOEE: {
                  ...i,
                },
              });
              return;
            }
            data.push({
              ...i,
              id,
              machineStatus: {
                status: 'Active',
              },
              machineStatisticsOnly: true,
              machineOEE: {
                ...i,
              },
            } as MachineWithOEE);
          });

          // add machines that have no data in the selected period from the statistics API
          Object.values(storage).forEach((machine) => {
            if (machine.machineId != null && !processedIds.has(machine.machineId)) {
              data.push({
                ...machine,
                hasNoDataInPeriod: true,
                machineOEE: null,
              });
            }
          });

          if (selectedItem.needUpdateAfterLoad) {
            // Get current URL to check if this machine is still the intended selection
            const routeParams = navigationRef?.current?.getCurrentRoute()?.params as { id?: string | number } | undefined;
            const urlMachineId = routeParams?.id;
            const currentMachineId = urlMachineId ? Number(urlMachineId) : null;

            // Only restore if this machine matches the current URL parameter
            if (currentMachineId === selectedItem.id) {
              const exist = data.find((i) => i.id === selectedItem.id);
              if (exist) {
                dispatch(setSelectedMachine({ ...exist, needUpdateAfterLoad: false }));
              }
            }
          }

          dispatch(setMachineListStatistic({ data: data || [], loaded: true }));
        },
      })
    )
  );
};

export const getMachineStatsOeeTeepPerPeriod = (data: OeeTeepStatisticsInput, signal?: AbortSignal): AppThunk<Promise<MachineOeeTeepPerPeriodStatistic[]>> => async (dispatch, getState) => {
  const {
    machinesV2: { selectedGroup },
  } = getState() as RootState;
  const machineGroupId = getPositiveNumberParam(data.machineGroupId);
  const params: MachineStatisticsOeeTeepRequestDto = buildOeeTeepGraphParams({
    ...data,
    machineGroupId: machineGroupId ?? (data.forAllMachines || data.allSelected ? selectedGroup?.machineGroupId : undefined),
  });

  const response = await dispatch(
    backendCall(
      createApiCall((api) => api.statistics.machineStatisticsOeeTeep, {
        params,
        signal,
      })
    )
  );

  return (response?.machineStatistics ?? []) as MachineOeeTeepPerPeriodStatistic[];
};

export const ncFileStatisticsPaid = (data: NcFileStatisticsPaidRequestDto, signal?: AbortSignal): AppThunk<Promise<NcFileStatisticsPaidResponse | undefined>> => async (dispatch) => {
  return await dispatch(
    backendCall(
      createApiCall((api) => api.statistics.ncFileStatisticsPaid, {
        params: data,
        signal,
      })
    )
  );
};

// TODO: machineStatisticsPaid - verify when backend is populated
export const machineStatisticsPaid = (data: MachineStatisticsPaidRequestDto, signal?: AbortSignal): AppThunk<Promise<MachineStatisticsPaidResponse | undefined>> => async (dispatch) => {
  return await dispatch(
    backendCall(
      createApiCall((api) => api.statistics.machineStatisticsPaid, {
        params: data,
        signal,
      })
    )
  );
};

export const machineEventCycleStatistics = (data: MachineEventCycleStatisticsRequestDto, signal?: AbortSignal): AppThunk<Promise<MachineEventCycleStatisticsResponse | undefined>> => async (dispatch) => {
  return await dispatch(
    backendCall(
      createApiCall((api) => api.statistics.machineEventCycleStatistics, {
        params: data,
        signal,
      })
    )
  );
};

// === Reducer ===

export default createReducer<StatisticsState>(JSON.parse(JSON.stringify(initState)), (builder) => {
  builder
    .addCase(setMachineListStatistic, (state, action) => ({
      ...state,
      machineListOEE: action.payload,
    }))
    .addCase(clear, () => {
      return initState;
    })
    .addCase(close, () => {
      return initState;
    });
});
